import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import * as React from 'react';
import { Plane } from 'lucide-react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Drawer, DrawerContent, DrawerTrigger } from '../ui/drawer';
import { POPULAR_CITIES } from '@/lib/contants';
import { Input } from '../ui/input';
import { useLazySearchCityQuery } from '@flysync/shared-redux/services/booking';
import {
  updateField,
  isCityPicker,
  updateReqData,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useDispatch, useSelector } from 'react-redux';
import { config } from '@/config';

interface CityPickerI {
  readonly type: string;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly state: {
    city_name: string;
    airport_name: string;
    country_name: string;
  };
  readonly setState: (
    city_name: string,
    airport_name: string,
    country_name: string,
    airport_code: string,
  ) => void;
}

interface CitiesI {
  city_name: string;
  airport_name: string;
  airport_code: string;
  country_name: string;
}

function CityPicker({ type, children, setState, className }: CityPickerI) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [open, setOpen] = React.useState(false);
  const [searchCity, { data: cityData }] = useLazySearchCityQuery();
  const [searchText, setSearchText] = React.useState<string>('');
  const dispatch = useDispatch();
  const stateData = useSelector((state: any) => state.flightSearch);

  // Initialize with fallback values for SSR
  const [originRecentSearch, setOriginRecentSearch] = React.useState(POPULAR_CITIES);
  const [destinationRecentSearch, setDestinationRecentSearch] = React.useState(POPULAR_CITIES);

  // Load recent searches from sessionStorage on client side
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const originData = sessionStorage.getItem('originRecentSearch');
      const destinationData = sessionStorage.getItem('destinationRecentSearch');
      
      if (originData && config.recentSearch === 'Y') {
        const parsedOrigin = JSON.parse(originData);
        if (parsedOrigin.length > 0) {
          setOriginRecentSearch(parsedOrigin);
        }
      }
      
      if (destinationData && config.recentSearch === 'Y') {
        const parsedDestination = JSON.parse(destinationData);
        if (parsedDestination.length > 0) {
          setDestinationRecentSearch(parsedDestination);
        }
      }
    }
  }, []);

  const onSelectCity = (city: CitiesI) => {
    setState(
      city.city_name,
      city.airport_name,
      city.country_name,
      city.airport_code,
    );
    dispatch(
      updateField({
        field: type === 'From' ? 'origin' : 'destination',
        value: city.airport_code,
      }),
    );
    dispatch(
      updateField({
        field: type === 'From' ? 'originCountry' : 'destinationCountry',
        value: city.country_name,
      }),
    );
    dispatch(
      updateField({
        field: type === 'From' ? 'origin_city' : 'destination_city',
        value: city.city_name,
      }),
    );
    dispatch(
      updateField({
        field: type === 'From' ? 'origin_airport' : 'destination_airport',
        value: city.airport_name,
      }),
    );
    setOpen(false);
  };

  // Debounced citysearch function
  const citySearch = (text: string) => {
    setSearchText(text);
    searchCity({ searchTerm: text });
    dispatch(isCityPicker(true));

    // Update state with entered text
    // setState(text, '', '', '');

    // // Update Redux store
    // if (type === 'From') {
    //   dispatch(
    //     updateField({
    //       field: 'origin',
    //       value: text,
    //     }),
    //   );
    //   dispatch(
    //     updateField({
    //       field: 'originCountry',
    //       value: '',
    //     }),
    //   );
    //   dispatch(
    //     updateField({
    //       field: 'origin_city',
    //       value: text,
    //     }),
    //   );
    // } else if (type === 'To') {
    //   dispatch(
    //     updateField({
    //       field: 'destination',
    //       value: text,
    //     }),
    //   );
    //   dispatch(
    //     updateField({
    //       field: 'destinationCountry',
    //       value: '',
    //     }),
    //   );
    //   dispatch(
    //     updateField({
    //       field: 'destination_city',
    //       value: text,
    //     }),
    //   );
    // }
  };

  const onCloseCallback = () => {
    setSearchText('');
    setOpen(false);
    dispatch(isCityPicker(false));
  };

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className={className}>
          <div onClick={() => setOpen(true)}>{children}</div>
        </PopoverTrigger>
        <PopoverContent
          className="bg-tertiary-light w-[240px] justify-start p-0"
          align="start"
        >
          <StatusList
            searchCity={citySearch}
            onCloseCallback={onCloseCallback}
            setSelectedStatus={onSelectCity}
            data={
              searchText !== ''
                ? cityData?.status?.data
                : type === 'From'
                  ? originRecentSearch
                  : destinationRecentSearch
            }
          />
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild className={className}>
        <div onClick={() => setOpen(true)}>{children}</div>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mt-4 border-t">
          <StatusList
            searchCity={citySearch}
            onCloseCallback={onCloseCallback}
            setSelectedStatus={onSelectCity}
            data={
              searchText !== ''
                ? cityData?.status?.data
                : type === 'From'
                  ? originRecentSearch
                  : destinationRecentSearch
            }
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function StatusList({
  setSelectedStatus,
  searchCity,
  data,
  onCloseCallback,
}: Readonly<{
  setSelectedStatus: (status: CitiesI) => void;
  searchCity: (text: string) => void;
  data: CitiesI[];
  onCloseCallback: () => void;
}>) {
  return (
    <Command>
      <Input
        placeholder="Search City"
        onInput={(e: React.FormEvent<HTMLInputElement>) =>
          searchCity(e.currentTarget.value)
        }
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup>
          {data?.length > 0 &&
            data.map((status) => (
              <CommandItem
                key={status.airport_code}
                value={status.airport_name}
                onSelect={() => {
                  setSelectedStatus(status);
                  onCloseCallback();
                }}
              >
                <div className="flex w-full flex-row items-center justify-center">
                  <div className="mr-2 rounded-full bg-primary p-1">
                    <Plane color="white" size={20} />
                  </div>
                  <div className="flex w-5/6 flex-col">
                    <h3 className="truncate text-sm font-medium">
                      {status.city_name}
                    </h3>
                    <h6 className="overflow-hidden text-ellipsis text-xs">
                      {status.airport_name}
                    </h6>
                  </div>
                  <div className="flex w-5 text-xs">
                    <h6>{status.airport_code}</h6>
                  </div>
                </div>
              </CommandItem>
            ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

export default CityPicker;
